import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Leaf, MessageSquare, Loader2, Copy, Check, LogIn, ArrowLeft, Lock } from 'lucide-react';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../../context/AuthContext';
import Sidebar from '../../components/Sidebar';

const API_URL = import.meta.env.VITE_API_URL || '';
const PUBLIC_URL = import.meta.env.VITE_PUBLIC_URL || '';

// Segment IDs - must match AskNaturalOrganicChat.tsx
const NATURAL_ORGANIC_ID = Number(import.meta.env.VITE_NATURAL_ORGANIC_SEGMENT_ID || 222);
const FLAVCITY_ID = Number(import.meta.env.VITE_FLAVCITY_SEGMENT_ID || 385);
const KNF_SURVEY_ID = Number(import.meta.env.VITE_KNF_SURVEY_SEGMENT_ID || 390);
const SMARTYPANTS_RAW_ID = Number(import.meta.env.VITE_SMARTYPANTS_RAW_SEGMENT_ID || 391);
const SMARTYPANTS_SYNTH_ID = Number(import.meta.env.VITE_SMARTYPANTS_SYNTH_SEGMENT_ID || 392);
const SMARTYPANTS_DT_ID = Number(import.meta.env.VITE_SMARTYPANTS_DT_SEGMENT_ID || 393);
const COOKIE_CONSUMER_ID = Number(import.meta.env.VITE_COOKIE_CONSUMER_SEGMENT_ID || 394);

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  personas_cited?: string[];
  format?: string;
}

interface SharedChatData {
  session_id: number;
  title: string | null;
  messages: ChatMessage[];
  segment_id: number;
  created_at: string;
  shared_at: string;
  can_fork: boolean;
  view_count: number;
  is_owner: boolean;
  has_segment_access: boolean;
}

// Same markdown components as AskNaturalOrganicChat
const markdownComponents: Components = {
  h1: ({ children }) => <h1 className="text-xl font-bold text-[#1C1C1E] mt-4 mb-2">{children}</h1>,
  h2: ({ children }) => <h2 className="text-lg font-bold text-[#1C1C1E] mt-4 mb-2">{children}</h2>,
  h3: ({ children }) => <h3 className="text-base font-semibold text-[#1C1C1E] mt-3 mb-1">{children}</h3>,
  h4: ({ children }) => <h4 className="text-sm font-semibold text-[#1C1C1E] mt-2 mb-1">{children}</h4>,
  p: ({ children }) => <p className="text-sm text-[#374151] mb-3 leading-relaxed">{children}</p>,
  ul: ({ children }) => <ul className="list-disc list-outside ml-5 mb-3 space-y-1">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal list-outside ml-5 mb-3 space-y-1">{children}</ol>,
  li: ({ children }) => <li className="text-sm text-[#374151] leading-relaxed">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-[#1C1C1E]">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-[#07E5D1] pl-4 py-1 my-3 bg-[#FAFBFC] rounded-r-lg">
      {children}
    </blockquote>
  ),
  code: ({ children, className }) => {
    const isInline = !className;
    return isInline ? (
      <code className="bg-[#FAFBFC] text-[#374151] px-1.5 py-0.5 rounded text-xs font-mono">{children}</code>
    ) : (
      <code className="block bg-gray-900 text-gray-100 p-3 rounded-lg text-xs font-mono overflow-x-auto my-3">
        {children}
      </code>
    );
  },
  pre: ({ children }) => <pre className="my-3">{children}</pre>,
  a: ({ href, children }) => (
    <a href={href} className="text-[#07E5D1] hover:underline" target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
  hr: () => <hr className="my-4 border-[#E5E7EB]" />,
  table: ({ children }) => (
    <div className="overflow-x-auto my-4 rounded-lg border border-[#E5E7EB]">
      <table className="min-w-full text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-[#FAFBFC] border-b border-[#E5E7EB]">{children}</thead>,
  tbody: ({ children }) => <tbody className="divide-y divide-[#F1F5F9]">{children}</tbody>,
  tr: ({ children }) => <tr className="hover:bg-[#FAFBFC] transition-colors">{children}</tr>,
  th: ({ children }) => (
    <th className="px-4 py-2.5 text-left text-xs font-semibold text-[#374151] uppercase tracking-wider">{children}</th>
  ),
  td: ({ children }) => (
    <td className="px-4 py-2.5 text-sm text-[#374151] align-top">{children}</td>
  ),
};

const SharedChatView: React.FC = () => {
  const { shareToken } = useParams<{ shareToken: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isAdmin, isFlavcityUser, isKnfUser, isSmartypantsUser, isDemoUser } = useAuth();

  // Check if user has access to a specific segment
  const hasAccessToSegment = (segmentId: number): boolean => {
    // Admins have access to all segments
    if (isAdmin) return true;

    // Natural Organic is public - everyone has access
    if (segmentId === NATURAL_ORGANIC_ID) return true;

    // Check segment-specific access
    if (segmentId === FLAVCITY_ID && isFlavcityUser) return true;
    if (segmentId === KNF_SURVEY_ID && isKnfUser) return true;
    if ([SMARTYPANTS_RAW_ID, SMARTYPANTS_SYNTH_ID, SMARTYPANTS_DT_ID].includes(segmentId) && isSmartypantsUser) return true;
    if (segmentId === COOKIE_CONSUMER_ID && isDemoUser) return true;

    return false;
  };
  const chatScrollRef = useRef<HTMLDivElement>(null);
  const hasAutoForked = useRef(false);

  const [sharedChat, setSharedChat] = useState<SharedChatData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isForking, setIsForking] = useState(false);
  const [copiedMessageIndex, setCopiedMessageIndex] = useState<number | null>(null);

  useEffect(() => {
    const fetchSharedChat = async () => {
      if (!shareToken) {
        setError('Invalid share link');
        setIsLoading(false);
        return;
      }

      try {
        // Only send credentials if authenticated (to check ownership)
        const fetchOptions: RequestInit = isAuthenticated
          ? { credentials: 'include' }
          : {};

        const res = await fetch(`${API_URL}/healthcare-chat-v2/shared/${shareToken}`, fetchOptions);
        if (!res.ok) {
          if (res.status === 404) {
            setError('This shared chat was not found or has expired');
          } else {
            setError('Failed to load shared chat');
          }
          setIsLoading(false);
          return;
        }

        const data = await res.json();
        setSharedChat(data);
      } catch (err) {
        console.error('Error fetching shared chat:', err);
        setError('Failed to load shared chat');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSharedChat();
  }, [shareToken, isAuthenticated]);

  // Auto-fork if returning from login with fork=true param
  useEffect(() => {
    const shouldAutoFork = searchParams.get('fork') === 'true';

    if (
      shouldAutoFork &&
      isAuthenticated &&
      sharedChat &&
      !sharedChat.is_owner &&
      sharedChat.can_fork &&
      hasAccessToSegment(sharedChat.segment_id) &&
      !hasAutoForked.current &&
      !isForking
    ) {
      hasAutoForked.current = true;
      // Trigger fork
      (async () => {
        setIsForking(true);
        try {
          const res = await fetch(`${API_URL}/healthcare-chat-v2/shared/${shareToken}/fork`, {
            method: 'POST',
            credentials: 'include',
          });
          if (res.ok) {
            const data = await res.json();
            navigate(`/ask-natural-organic/${data.new_session_id}`, { replace: true });
          } else if (res.status === 403) {
            // User doesn't have access - update local state
            setSharedChat(prev => prev ? { ...prev, has_segment_access: false, can_fork: false } : null);
          }
        } catch (err) {
          console.error('Auto-fork failed:', err);
        } finally {
          setIsForking(false);
        }
      })();
    }
  }, [searchParams, isAuthenticated, sharedChat, shareToken, navigate, isForking]);

  const handleFork = async () => {
    if (!isAuthenticated) {
      // Redirect to login with fork=true so we auto-fork after login
      navigate(`/auth?redirect=/chat/shared/${shareToken}?fork=true`);
      return;
    }

    setIsForking(true);
    try {
      const res = await fetch(`${API_URL}/healthcare-chat-v2/shared/${shareToken}/fork`, {
        method: 'POST',
        credentials: 'include',
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        if (res.status === 403) {
          // User doesn't have access - update local state to reflect this
          setSharedChat(prev => prev ? { ...prev, has_segment_access: false, can_fork: false } : null);
          alert('You don\'t have access to this audience. Contact your administrator for access.');
        } else {
          console.error('Failed to fork chat:', errorData.detail || 'Unknown error');
        }
        return;
      }

      const data = await res.json();
      navigate(`/ask-natural-organic/${data.new_session_id}`);
    } catch (err) {
      console.error('Error forking chat:', err);
    } finally {
      setIsForking(false);
    }
  };

  const handleCopyMessage = async (content: string, index: number) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedMessageIndex(index);
      setTimeout(() => setCopiedMessageIndex(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (isLoading) {
    return (
      <div className="h-screen bg-[#FEFEFE] flex overflow-hidden">
        <Sidebar />
        <div className="flex-1 flex flex-col md:ml-16 overflow-hidden">
          <div className="flex-1 flex items-center justify-center">
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="w-8 h-8 text-[#07E5D1] animate-spin" />
              <p className="text-sm text-[#6B7280]">Loading shared chat...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !sharedChat) {
    return (
      <div className="h-screen bg-[#FEFEFE] flex overflow-hidden">
        <Sidebar />
        <div className="flex-1 flex flex-col md:ml-16 overflow-hidden">
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center max-w-md px-4">
              <div className="w-16 h-16 rounded-full bg-[#FEF2F2] flex items-center justify-center mx-auto mb-4">
                <Leaf className="w-8 h-8 text-[#EF4444]" />
              </div>
              <h1 className="text-xl font-semibold text-[#1C1C1E] mb-2">Chat Not Found</h1>
              <p className="text-[#6B7280] mb-6">{error || 'This shared chat could not be loaded.'}</p>
              <a
                href="/"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#07E5D1] text-white rounded-lg hover:bg-[#06D4C2] transition-colors font-medium"
              >
                Go Home
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-[#FEFEFE] flex overflow-hidden">
      <Sidebar />
      <div className="flex-1 flex flex-col md:ml-16 overflow-hidden">
        {/* Header - same as AskNaturalOrganicChat */}
        <header className="bg-white border-b border-[#F1F5F9] px-6 py-3 mt-16 md:mt-0">
          <div className="max-w-[1600px] mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-[#07E5D1] flex items-center justify-center text-white">
                <Leaf className="w-4.5 h-4.5" />
              </div>
              <div>
                <h1 className="font-semibold text-[#1C1C1E] text-sm">
                  {sharedChat.title || 'Shared Chat'}
                </h1>
                <p className="text-xs text-[#9CA3AF]">
                  Shared conversation ({sharedChat.messages.length} messages)
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Owner: Back to Chat button */}
              {sharedChat.is_owner ? (
                <button
                  onClick={() => navigate(`/ask-natural-organic/${sharedChat.session_id}`)}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-white bg-[#07E5D1] hover:bg-[#06D4C2] transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Chat
                </button>
              ) : isAuthenticated && !hasAccessToSegment(sharedChat.segment_id) ? (
                /* No access indicator */
                <span className="flex items-center gap-1.5 px-4 py-2 text-sm text-[#6B7280]">
                  <Lock className="w-4 h-4" />
                  No Access
                </span>
              ) : sharedChat.can_fork && (!isAuthenticated || hasAccessToSegment(sharedChat.segment_id)) && (
                /* Continue button for non-owners with access */
                <button
                  onClick={handleFork}
                  disabled={isForking}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-white bg-[#07E5D1] hover:bg-[#06D4C2] transition-colors disabled:opacity-50"
                >
                  {isForking ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : isAuthenticated ? (
                    <MessageSquare className="w-4 h-4" />
                  ) : (
                    <LogIn className="w-4 h-4" />
                  )}
                  {isForking ? 'Continuing...' : isAuthenticated ? 'Continue Chat' : 'Sign in to Continue'}
                </button>
              )}

              <a href="/" className="flex items-center hover:opacity-80 transition-opacity">
                <img src={`${PUBLIC_URL}/logo.png`} alt="BluePill" className="h-16" />
              </a>
            </div>
          </div>
        </header>

        {/* Body: chat only (no sidebar for shared view) */}
        <div className="flex-1 flex overflow-hidden">
          {/* Center — Chat */}
          <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
            <div ref={chatScrollRef} className="flex-1 overflow-y-auto">
              <div className="max-w-[960px] mx-auto px-6 py-6">
                {/* Messages - same styling as AskNaturalOrganicChat */}
                <div className="space-y-5">
                  {sharedChat.messages.map((msg, idx) => (
                    <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div
                        className={`rounded-2xl px-5 py-4 ${
                          msg.role === 'user'
                            ? 'max-w-[85%] bg-[#07E5D1] text-white rounded-br-md'
                            : 'max-w-full bg-white border border-[#E5E7EB] text-[#374151] rounded-bl-md shadow-sm'
                        }`}
                      >
                        {msg.role === 'assistant' ? (
                          <div className="max-w-none">
                            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                              {msg.content}
                            </ReactMarkdown>
                            <div className="flex justify-end mt-3 pt-2 border-t border-[#F1F5F9]">
                              <button
                                onClick={() => handleCopyMessage(msg.content, idx)}
                                className={`flex items-center gap-1.5 px-2 py-1 text-xs transition-colors rounded ${
                                  copiedMessageIndex === idx
                                    ? 'text-[#10B981] bg-[#ECFDF5]'
                                    : 'text-[#9CA3AF] hover:text-[#07E5D1] hover:bg-[#FAFBFC]'
                                }`}
                                title="Copy to clipboard"
                              >
                                {copiedMessageIndex === idx ? (
                                  <>
                                    <Check className="w-3.5 h-3.5" />
                                    Copied!
                                  </>
                                ) : (
                                  <>
                                    <Copy className="w-3.5 h-3.5" />
                                    Copy
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Bottom CTA */}
                {sharedChat.is_owner ? (
                  <div className="mt-8 p-6 bg-[#E8FCFA] rounded-xl border border-[#07E5D1]/20 text-center">
                    <h3 className="font-semibold text-[#1C1C1E] mb-2">
                      This is your chat
                    </h3>
                    <p className="text-sm text-[#6B7280] mb-4">
                      Go back to your chat to continue the conversation.
                    </p>
                    <button
                      onClick={() => navigate(`/ask-natural-organic/${sharedChat.session_id}`)}
                      className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#07E5D1] text-white rounded-lg hover:bg-[#06D4C2] transition-colors font-medium"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Back to Chat
                    </button>
                  </div>
                ) : isAuthenticated && !hasAccessToSegment(sharedChat.segment_id) ? (
                  <div className="mt-8 p-6 bg-[#FEF2F2] rounded-xl border border-[#FECACA] text-center">
                    <div className="w-12 h-12 rounded-full bg-[#FEE2E2] flex items-center justify-center mx-auto mb-3">
                      <Lock className="w-6 h-6 text-[#EF4444]" />
                    </div>
                    <h3 className="font-semibold text-[#1C1C1E] mb-2">
                      Access Restricted
                    </h3>
                    <p className="text-sm text-[#6B7280]">
                      You don't have access to chat with this audience. Contact your administrator for access.
                    </p>
                  </div>
                ) : sharedChat.can_fork && (!isAuthenticated || hasAccessToSegment(sharedChat.segment_id)) && (
                  <div className="mt-8 p-6 bg-[#E8FCFA] rounded-xl border border-[#07E5D1]/20 text-center">
                    <h3 className="font-semibold text-[#1C1C1E] mb-2">
                      Want to continue this conversation?
                    </h3>
                    <p className="text-sm text-[#6B7280] mb-4">
                      Continue this conversation in your own account.
                    </p>
                    <button
                      onClick={handleFork}
                      disabled={isForking}
                      className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#07E5D1] text-white rounded-lg hover:bg-[#06D4C2] transition-colors font-medium disabled:opacity-50"
                    >
                      {isForking ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : isAuthenticated ? (
                        <MessageSquare className="w-4 h-4" />
                      ) : (
                        <LogIn className="w-4 h-4" />
                      )}
                      {isForking ? 'Continuing...' : isAuthenticated ? 'Continue Chat' : 'Sign in to Continue'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default SharedChatView;
